import React, { useState, useRef } from 'react';

import {
	Wrapper,
	CellNumber,
	CellNumberCheckbox,
	Cell,
	SortArrow,
	SortIconSpace,
	CheckboxWrapper,
	HeaderCellWrapper,
	Content,
	HeaderScrollWrapper,
	HeaderText,
	RowSpan,
	SpanColumn,
	SpanWrapper,
} from './ItemsTable.styles';
import { ApCheckbox } from '@alixpartners/ui-components';
import { Text, Row } from './TableRow/TableRow.styles';
import { ITEMS_TYPE, sortGroups, getLeaves } from '../../utils';
import { VirtualList } from './VirtualList';

const SORT_DIRECTION = {
	ASC: 'asc',
	DESC: 'desc',
};

export const ItemsTable = ({
	data,
	companies,
	itemsType,
	spendFormatter,
	onItemsChange,
	items,
	highlightId,
	showSegments,
	segments,
	showTopVendors,
	onTailItemsChange,
}) => {
	const [sort, setSort] = useState({});
	const wrapperRef = useRef();
	const headerRef = useRef();

	const renderSortArrow = column => {
		if (!sort || sort.column !== column) {
			return <SortIconSpace />;
		}

		return (
			<SortArrow
				iconName="baseline_arrow_forward"
				iconSize={13}
				reverse={sort.direction === SORT_DIRECTION.DESC}
			/>
		);
	};

	const onSortChange = (column, isCompany = false) => {
		let newSort = { ...sort };

		if (sort.column === column) {
			if (sort.direction === SORT_DIRECTION.ASC) {
				newSort.direction = SORT_DIRECTION.DESC;
			} else {
				newSort = {};
			}
		} else {
			newSort = {
				column,
				direction: SORT_DIRECTION.ASC,
				isCompany,
			};
		}

		setSort(newSort);
	};

	const isGrouped = data.length && data[0].children && data[0].children.length;
	const sortedData = sortGroups(data, sort);

	const handleItemChange = data => {
		let updatedItems = [];
		const currentIndex = items.findIndex(
			item => item.name === data.name && item.groupName === data.groupName
		);

		if (currentIndex === -1) {
			updatedItems = [...items, data];
		} else {
			updatedItems = [
				...items.slice(0, currentIndex),
				...items.slice(currentIndex + 1),
			];
		}

		onItemsChange(updatedItems);
	};

	const handleGroupItemChange = ({ children }) => {
		let updatedItems = [];

		if (children && children.length) {
			const isGroupSelected = children.every(item =>
				items.some(
					node => node.name === item.name && node.groupName === item.groupName
				)
			);

			if (isGroupSelected) {
				updatedItems = items.filter(
					item =>
						!children.some(
							node =>
								node.name === item.name && node.groupName === item.groupName
						)
				);
			} else {
				const notSelectedItems = children.filter(
					item =>
						!items.some(
							node =>
								node.name === item.name && node.groupName === item.groupName
						)
				);
				updatedItems = [...items, ...notSelectedItems];
			}
		}

		onItemsChange(updatedItems);
	};

	const allItems = getLeaves(data);
	const isAllChecked =
		items.length > 0 &&
		allItems.every(item =>
			items.some(
				node => node.name === item.name && node.groupName === item.groupName
			)
		);

	const handleAllItemsChange = () => {
		onItemsChange(isAllChecked ? [] : allItems);
	};

	const isPartiallyChecked =
		items.length > 0 &&
		allItems.some(
			item =>
				!items.some(
					node => node.name === item.name && node.groupName === item.groupName
				)
		);

	const isTailAllChecked = allItems.every(item => item.includeTailSpend);
	const isTailPartialyChecked =
		!isTailAllChecked && allItems.some(item => item.includeTailSpend);

	return (
		<Wrapper>
			<HeaderScrollWrapper innerRef={headerRef}>
				<Row>
					<Cell>
						<CheckboxWrapper>
							<ApCheckbox
								onClick={handleAllItemsChange}
								onChange={() => {}}
								checked={isAllChecked}
								indeterminate={isPartiallyChecked}
							/>
						</CheckboxWrapper>
						<HeaderCellWrapper onClick={() => onSortChange('name')}>
							{itemsType === ITEMS_TYPE.VENDOR ? 'Vendors' : 'L4 categories'}

							{renderSortArrow('name')}
						</HeaderCellWrapper>
					</Cell>
					<CellNumber onClick={() => onSortChange('spend')}>
						{itemsType === ITEMS_TYPE.TAIL_SPEND
							? 'Tail spend'
							: itemsType === ITEMS_TYPE.CATEGORY
								? 'Spend'
								: 'Total spend'}
						{renderSortArrow('spend')}
					</CellNumber>
					{itemsType === ITEMS_TYPE.CATEGORY && (
						<React.Fragment>
							<CellNumber onClick={() => onSortChange('headSpend')}>
								Head spend{renderSortArrow('headSpend')}
							</CellNumber>
							<CellNumberCheckbox onClick={() => onSortChange('tailSpend')}>
								<CheckboxWrapper
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										onTailItemsChange(allItems);
									}}
								>
									<ApCheckbox
										onClick={() => {}}
										onChange={() => {}}
										checked={isTailAllChecked}
										indeterminate={isTailPartialyChecked}
									/>
								</CheckboxWrapper>
								Tail spend (tick to include to spend){renderSortArrow(
									'tailSpend'
								)}
							</CellNumberCheckbox>
						</React.Fragment>
					)}

					{itemsType === ITEMS_TYPE.TAIL_SPEND &&
						showSegments &&
						segments && (
							<SpanWrapper>
								<RowSpan>
									<HeaderText>Segment</HeaderText>
								</RowSpan>
								<RowSpan>
									{segments.map(({ name }) => (
										<SpanColumn key={name}>
											<HeaderText>{name}</HeaderText>
										</SpanColumn>
									))}
								</RowSpan>
							</SpanWrapper>
						)}
					{companies.map(companyName => (
						<React.Fragment key={companyName}>
							<CellNumber
								onClick={() => onSortChange(companyName, true)}
								title={companyName}
							>
								<Text>{companyName}</Text>
								{renderSortArrow(companyName)}
							</CellNumber>
							{itemsType === ITEMS_TYPE.TAIL_SPEND &&
								segments &&
								showSegments && (
									<SpanWrapper>
										<RowSpan>
											<HeaderText>Segment</HeaderText>
										</RowSpan>
										<RowSpan>
											{segments.map(({ name }) => (
												<SpanColumn key={name}>
													<HeaderText>{name}</HeaderText>
												</SpanColumn>
											))}
										</RowSpan>
									</SpanWrapper>
								)}
						</React.Fragment>
					))}
				</Row>
			</HeaderScrollWrapper>
			<Content innerRef={wrapperRef}>
				{!isGrouped ? (
					<VirtualList
						wrapperRef={wrapperRef}
						headerRef={headerRef}
						data={sortedData}
						companies={companies}
						spendFormatter={spendFormatter}
						onItemChange={handleItemChange}
						onTailItemsChange={onTailItemsChange}
						items={items}
						highlightId={highlightId}
						segments={segments}
						showSegments={showSegments}
						itemsType={itemsType}
						showTopVendors={showTopVendors}
					/>
				) : (
					<VirtualList
						wrapperRef={wrapperRef}
						headerRef={headerRef}
						data={sortedData.reduce(
							(acc, item) => [...acc, item, ...item.children],
							[]
						)}
						companies={companies}
						spendFormatter={spendFormatter}
						onTailItemsChange={onTailItemsChange}
						onItemChange={handleItemChange}
						onGroupItemChange={handleGroupItemChange}
						items={items}
						highlightId={highlightId}
						segments={segments}
						showSegments={showSegments}
						itemsType={itemsType}
						showTopVendors={showTopVendors}
					/>
				)}
			</Content>
		</Wrapper>
	);
};
